/**
 * LocalizedCopyStatus — where one localized field stands between the
 * operator's own words and the model's: a pass asked for, a pass in flight, a
 * pass that wrote, or a pass that could not.
 *
 * Shared vocabulary, not an entity: `Event.descriptionByLocale`,
 * `TicketType.nameByLocale` and `TicketType.descriptionByLocale` each carry
 * one of these beside the map it describes.
 *
 * Saving is one whole gesture and asking is the other — a Firestore trigger
 * (`firebase/src/events/localize/localize.webhook.ts`) reads the source
 * string the operator typed and writes the map — so the document itself is
 * the only place two questions can be answered from: is the machine working
 * on this right now, and whose words are in which pane. Neither is cosmetic:
 * the storefront basket row IS the § 312j Abs. 2 BGB *wesentliche Merkmale*
 * disclosure, so a machine pane that drops a type's eligibility qualifier is
 * the operator's own exposure, and they can only catch it if the surface
 * tells them which words are theirs and whether a pass is about to replace
 * what they are reading.
 *
 * TWO WRITERS, AND THIS FIELD IS WHAT KEEPS THEM APART. The operator's editor
 * writes `requested` and nothing else; the trigger writes the other three and
 * never `requested`. That asymmetry is what makes the trigger's fire
 * condition unable to fire on the trigger's own work: a pass goes out when a
 * source string moved or when a field entered `requested`, and no write of
 * the trigger's own satisfies either.
 *
 * Variants justified:
 *   requested — the operator asking for a pass over copy nobody edited: a
 *     draft they want redone, a pass that produced nothing, a pass whose
 *     instance died mid-call. The field it sits on names which copy is being
 *     asked about, so the gesture carries nothing beside itself.
 *   translating — the pass has begun and the paid call is out. It is what the
 *     spinner beside the field reads, so an operator who opens the versions
 *     mid-pass is told the panes in front of them are about to be replaced,
 *     rather than correcting one that a write seconds away will overwrite.
 *     `startedAt` is when that call went out, and the trigger is its only
 *     reader: a marker younger than the trigger's own timeout belongs to a
 *     pass still running, so a request landing on it is answered by that pass
 *     instead of spending a second call, and one older belongs to an instance
 *     that died and will never write a terminal state, so a request landing
 *     on it is honoured. A marker that cannot say when its pass began cannot
 *     claim to be live, and reads as the second case.
 *   translated — the model wrote every pane. `sourceLocale` names the one
 *     pane that restates the operator's own words; every other pane is a
 *     translation of them. Nothing else on the document carries that fact —
 *     the source-locale pane and the source string hold the same words, so
 *     provenance is unreadable from the text, and a map alone leaves *which
 *     words are mine* unanswerable at the exact surface the disclosure
 *     obligation sits on.
 *   failed — the pass produced nothing, and the operator holds a subject
 *     with no translation. It carries no reason: nothing this copy passes
 *     through is bounded by a length any more, so the one failure an
 *     operator could act on differently — a pane too long for the field to
 *     store — has no way to occur, and the seam's remaining classes (a
 *     response that fails its own output contract, and one that never
 *     arrives) are answered by the same gesture, asking again. The condition
 *     that fired is logged where it happened.
 *
 * Excluded:
 *   a hand-corrected state — a correction is bound to the sentence it
 *     corrected, so a source edit rewrites every pane, corrections included,
 *     and the editor states that rule where the correction is made. A state
 *     pinning one pane against a later source edit would keep serving buyers
 *     copy the operator has already withdrawn, with nobody told.
 *   who asked, and when — a request is answered, folded into a running pass,
 *     or superseded inside one trigger invocation, and no surface renders its
 *     author. `startedAt` sits on `translating` rather than here because what
 *     the trigger weighs is the age of the call it might duplicate, not the
 *     age of the asking.
 *   a locale on `failed` — a pass covers a field's locales together and
 *     fails as one, so there is no pane to name.
 *   a per-locale status map — a pass covers every locale of one field at
 *     once, so every state here is field-wide by construction.
 *   a retry count — nothing re-attempts a pass on its own. Saving the source
 *     again and asking again are the two retries, and both are the operator's.
 */
import { LocaleEnum } from "@momo/i18n";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";

export const LocalizedCopyStatusSchema = z.discriminatedUnion("state", [
	z.object({ state: z.literal("requested") }),
	z.object({
		state: z.literal("translating"),
		startedAt: z
			.custom<Timestamp>()
			.nullish()
			.describe(
				"when the paid call for this pass went out — the trigger weighs it against its own instance timeout to tell a pass that is still running from one whose instance died, and absent reads as the second",
			),
	}),
	z.object({
		state: z.literal("translated"),
		sourceLocale: LocaleEnum.describe(
			"the language the operator typed the source string in — that locale's pane restates their own words, and every other pane is the model's translation of them",
		),
	}),
	z.object({ state: z.literal("failed") }),
]);
export type LocalizedCopyStatus = z.infer<typeof LocalizedCopyStatusSchema>;
